import EventBus from '../../EventsBus';
import { Field, FieldContext } from '../field';
import { VesselGlobal, SharedGlobal } from '../../forms/types/FormDefinition';
import { FieldDefinition } from '../types/FieldDefinition';

export class ConsumptionField extends Field {
  private initialisedFields: Field[];
  private fuelTypes: VesselGlobal[];
  private requiredGlobals: {
    [key: string]: SharedGlobal | undefined;
  };
  private listeners: {
    key: string;
    handler: any;
  }[];

  private calcConsumptionValidationEnabled = true;

  constructor({
    fieldDef, context,
  }: {
    fieldDef: FieldDefinition,
    context: FieldContext
  }) {
    super({
      fieldDef, context,
    });
    this.initialisedFields = context.initialisedFields;
    this.fuelTypes = context.vesselGlobals.filter((global) => global.type === 'fuel');
    this.listeners = [];

    this.requiredGlobals = {
      steamingHours: context.sharedGlobals.find(
        (global) => global.name === 'steamingHours',
      ),
      windForce: context.sharedGlobals.find(
        (global) => global.name === 'windForce',
      ),
      vesselCondition: context.sharedGlobals.find(
        (global) => global.name === 'vesselCondition',
      ),
      reportedSpeed: context.sharedGlobals.find(
        (global) => global.name === 'reportedSpeed',
      ),
      consPurposePropulsion: context.sharedGlobals.find(
        (global) => global.name === 'consPurposePropulsion',
      ),
      consPurposeAccommodation: context.sharedGlobals.find(
        (global) => global.name === 'consPurposeAccommodation',
      ),
    };

    // We need to see if the calculated consumption is ever negative and disable validation if it is
    // At the moment the only way to identify the calculated consumption fields is by searching the
    // field titles this is obviously brittle and in the future we should use compositie globals to
    // identify them
    // This needs to be in a set timeout so all the starting fields are initialised
    setTimeout(() => {
      const calculatedConsumptionFields = this.initialisedFields
        .filter((field) => field.fieldDef.type === 'computed' && field.fieldDef.title?.includes('Calculated consumption'));

      // Disable or enable the consumption validation if required on load
      const hasNegativeConsumptionValuesOnLoad = calculatedConsumptionFields
        .some((f) => f.getContent() < 0);
      this.setCalculatedConsumptionValidation(!hasNegativeConsumptionValuesOnLoad);

      calculatedConsumptionFields.forEach((field) => {
        this.listeners.push({
          key: `${field.id}:updated`,
          handler: () => {
            const hasNegativeConsumptionValues = calculatedConsumptionFields
              .some((f) => f.getContent() < 0);
            // Enable validation if there are no negatice values, disable it otherwise
            this.setCalculatedConsumptionValidation(!hasNegativeConsumptionValues);
          },
        });
      });
      // Listen to the events we are interested in
      this.listeners.forEach(({ key, handler }) => EventBus.$on(key, handler));


      // Disable ROB calculation on when there is an existing submission
      if (this.context.existingSubmission) {
        const robTotalFields = this.initialisedFields.filter((field) => field.fieldDef.title?.includes('ROB Total'));
        robTotalFields.forEach((field) => {
          if (!field.state.editable) {
            // eslint-disable-next-line no-param-reassign
            field.fieldDef.formulas = [];
          }
        });
        calculatedConsumptionFields.forEach((field) => {
          if (!field.state.editable) {
            // eslint-disable-next-line no-param-reassign
            field.fieldDef.formulas = [];
          }
        });
      }

      // Load and discharge fields should only be visible on departure forms,
      // and position report forms when the vessel is "In Port". They have a formula on them
      // to make them visible in port position reports, but we need to force them to be
      // visible in departures.
      if (this.context.formDef.type && this.context.formDef.type === 'departure') {
        const loadDischargeRows = this.initialisedFields.filter((field) => field.fieldDef.title?.includes('Load/Dis'));
        loadDischargeRows.forEach((field) => field.setVisibility(true));
      }

      // update recived fields manually again on the next tick.
      // This will set it dynamically from the bunker stems fields.
      const fuelReceivedFields = this.initialisedFields.filter((field) => field.fieldDef.title?.includes('- Received'));

      fuelReceivedFields.forEach((field) => {
        field.handleDependencyUpdated();
      });
    });
  }

  /**
   * Can be used to to enable or disable calc consumption validation
   * @param enabled {boolean}
   */
  private setCalculatedConsumptionValidation(enabled: boolean) {
    // If the validation is already disabled or enabled do nothing
    if (this.calcConsumptionValidationEnabled === enabled) {
      return;
    }

    this.calcConsumptionValidationEnabled = enabled;
    console.log(`Consumption validation ${enabled ? 'enabled' : 'disabled'}`);

    const children = this.formDef.fields
      .filter((field) => this.fieldDef.children?.includes(field.id));
    const consumptionTotalsRow = children
      .find((field) => `${field.type}` === 'consumption-label-totals');

    consumptionTotalsRow?.children!.forEach((id) => {
      const child = this.context.initialisedFields.find((field) => field.id === id);
      if (enabled) {
        // eslint-disable-next-line no-unused-expressions
        child?.enableValidation();
      } else {
        // eslint-disable-next-line no-unused-expressions
        child?.disableValidation();
      }
    });
  }

  /**
   * Get the current values for the globals this field needs
   */
  private getRequiredGlobalValues() {
    const values: any = {};

    Object.keys(this.requiredGlobals).forEach((key) => {
      const global = this.requiredGlobals[key];
      values[key] = global ? this.getCurrentGlobalValue(global.id) : null;
    });

    return values;
  }

  /**
   * Returns a numberic version of the windforce by extracting it from the
   * human readable string
   */
  windForce(): number {
    const globalValues = this.getRequiredGlobalValues();
    if (!globalValues.windForce) return 0;

    return parseInt(globalValues.windForce.substring(0, 2), 10);
  }

  /**
   * Get the current global value for the given id
   * @param globalId
   */
  private getCurrentGlobalValue(globalId: string) {
    const global = this.context.updatedGlobals.find((g) => g.globalId === globalId);
    return global?.value;
  }

  getConsumptionConfig(fuelType: string, speed: number): any {
    const configsForFuel = this.context.vesselConsumptionConfig.filter(
      (config) => config.fuel === fuelType,
    );

    // If there are no configs for the fuel return early
    if (configsForFuel.length === 0) {
      return null;
    }

    // Look for a config that matches the given speed
    const consumptionConfig = configsForFuel.find(
      (config) => config.speed === speed,
    );
    if (consumptionConfig) {
      return consumptionConfig;
    }

    // If we didn't find one because the speed is above or below the speeds available
    // find the closest one and return that instead.
    const minSpeed = configsForFuel.reduce((min, config) => {
      let newMin = min;
      if (Number(config.speed) < min) {
        newMin = Number(config.speed);
      }
      return newMin;
    }, 1000);
    const maxSpeed = configsForFuel.reduce((max, config) => {
      let newMax = max;
      if (Number(config.speed) > max) {
        newMax = Number(config.speed);
      }
      return newMax;
    }, 0);

    if (speed < minSpeed) {
      return configsForFuel.find((config) => config.speed === minSpeed);
    }

    if (speed > maxSpeed) {
      return configsForFuel.find((config) => config.speed === maxSpeed);
    }

    return null;
  }

  /**
   * Remove all listeners
   */
  destroy() {
    super.destroy();
    this.listeners.forEach(({ key, handler }) => {
      EventBus.$off(key, handler);
    });
  }
}
